import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  styled
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoIcon from '@mui/icons-material/Info';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#2a2a3c',
    color: '#ffffff',
    borderRadius: '8px',
    minWidth: '400px',
    maxWidth: '500px'
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: '#384664',
  padding: '16px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#2a2a3c'
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px',
  backgroundColor: '#2a2a3c',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)'
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '16px'
}));

export type ConfirmationDialogType = 'warning' | 'info' | 'error' | 'success';

export interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: ConfirmationDialogType;
  showIcon?: boolean;
  confirmButtonColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  loading?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Confirmar Ação',
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  type = 'warning',
  showIcon = true,
  confirmButtonColor = 'primary',
  loading = false
}) => {
  const getIcon = () => {
    const iconProps = { sx: { fontSize: 48 } };
    
    switch (type) {
      case 'warning':
        return <WarningAmberIcon {...iconProps} sx={{ ...iconProps.sx, color: '#ff9800' }} />;
      case 'info':
        return <InfoIcon {...iconProps} sx={{ ...iconProps.sx, color: '#2196f3' }} />;
      case 'error':
        return <ErrorIcon {...iconProps} sx={{ ...iconProps.sx, color: '#f44336' }} />;
      case 'success':
        return <CheckCircleIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4caf50' }} />;
      default:
        return <WarningAmberIcon {...iconProps} sx={{ ...iconProps.sx, color: '#ff9800' }} />;
    }
  };

  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch (error) {
      console.error('Error in confirmation action:', error);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
    >
      <StyledDialogTitle id="confirmation-dialog-title">
        <Typography variant="h6" component="div">
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
          disabled={loading}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {showIcon && (
          <IconContainer>
            {getIcon()}
          </IconContainer>
        )}
        <DialogContentText
          id="confirmation-dialog-description"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            textAlign: 'center',
            fontSize: '1rem',
            whiteSpace: 'pre-line'
          }}
        >
          {message}
        </DialogContentText>
      </StyledDialogContent>

      <StyledDialogActions>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.08)'
            }
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={confirmButtonColor}
          disabled={loading}
          autoFocus
        >
          {confirmText}
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default ConfirmationDialog;
